import { useState } from 'react';
import { useNotify, useCanAccess, useRecordContext } from 'ra-core';
import {
  exporterLesComptes,
  exporterLesCours,
  importerDesComptes,
} from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES DEUX EXPORTS ET L'IMPORT — F-66, ET IL N'Y EN A PAS UN TROISIÈME.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-66 : « **DEUX EXPORTS, ET DEUX SEULEMENT** : la liste des comptes rattachés à une
 * structure, et la liste de ses cours. **Aucun troisième**, et **l'export par une personne de
 * ses propres données reste refusé en V1**. »
 *
 * C'est pour cela que les listes de cette surface passent `exporter={false}` : l'exportateur
 * générique de la peau produirait le troisième, sur n'importe quelle liste, sans que rien ne
 * le signale.
 *
 * **L'export des cours est un geste de la personne qui tient l'espace, elle seule** — « un
 * tuteur n'exporte pas ses propres cours » —, ce que la garde de rôle de la route tient.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE CSV ARRIVE DANS UN CHAMP JSON, ET LE TÉLÉCHARGEMENT SE FAIT ICI — D-074.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-66 : « **remise par TÉLÉCHARGEMENT DIRECT DANS LE NAVIGATEUR** : rien n'est stocké,
 * **aucun lien à durée de vie, aucun envoi par email**. C'est précisément ce qui n'ajoute
 * aucune dépendance externe. »
 *
 * D-074 dit comment, et pourquoi pas autrement : le CSV voyage **en champ de texte dans le
 * corps JSON**, comme l'import de `CDC-1` le fait déjà dans l'autre sens. « Servir un
 * `text/csv` en flux aurait demandé de **sortir de la liste blanche à la sortie**, premier
 * mécanisme anti-fuite du projet, et de déclarer un type de réponse qu'**aucune route du
 * produit ne déclare**. »
 *
 * **Ce fichier est donc le seul endroit du dépôt qui fabrique un téléchargement.** Il le fait
 * avec un objet local et le révoque aussitôt : rien n'est stocké, et l'adresse temporaire ne
 * survit pas au geste — ce qui est la lecture littérale de « aucun lien à durée de vie ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **L'IMPORT N'A PAS DE ROUTE ICI, ET C'EST LE FAIT LE PLUS UTILE DU LOT — D-070.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-66, amendement du 20260824 : « **UN IMPORT, ET UN SEUL** : les comptes d'une structure.
 * **Il passe par `POST /api/v1/provisionnement/import`, F-04 de `CDC-1`, et par elle
 * seule.** Cette route existe, elle est livrée, et elle fait déjà tout ce que les règles
 * décrivent. **La surface d'administration l'appelle ; elle n'en porte pas une seconde.** »
 *
 * Le motif est celui de Q566 : « **deux chemins pour un geste font deux autorisations à
 * tenir d'accord, et celle qu'on oublie est la faille.** » Et D-070 ajoute ce que la classe
 * de défaut enseigne : « **l'import n'était pas des cinq pour une seule raison : personne
 * n'avait vu qu'il existait déjà.** »
 *
 * **ET LE GESTE N'EST OUVERT QU'À L'`orgadmin` DEPUIS LE 20260824 — D-082.** §6.14 l'accordait
 * aux deux ; le droit du compte qui voit tout est retiré, parce que la route déduit la
 * structure de l'appelant et qu'il n'en a aucune. Les deux autres issues — un paramètre de
 * structure, ou une seconde route — coûtaient toutes deux plus que le retrait : voir la note de
 * `importer` dans `matrice.ts`.
 *
 * **Ce que l'écran doit dire des refus, et F-66 en fait des critères.** « SI **une seule
 * ligne** du fichier d'import est invalide, ALORS LE SYSTÈME DOIT refuser **le fichier
 * entier** et **rendre toutes les erreurs d'un coup** » ; et « QUAND le même fichier est
 * passé deux fois, LE SYSTÈME DOIT ne créer **aucun doublon** — la reconnaissance se faisant
 * par adresse — **et écrire deux lignes de trace**, parce que le geste a eu lieu deux fois ».
 *
 * **Les trois gestes laissent une ligne de trace conservée**, et pour l'import cela corrige
 * un repli antérieur : « la symétrie s'est inversée, et **l'import est le geste qui crée des
 * comptes en masse : le moins traçable de tous serait le plus utile à retracer** ».
 */

/**
 * **Rendre un fichier au navigateur, sans rien stocker et sans lien qui survive.**
 *
 * La marque d'ordre est ajoutée devant le texte : F-66 tranche « **séparateur point-virgule
 * et encodage universel avec sa marque d'ordre**, pour qu'un tableur français ouvre le
 * fichier sans réglage ». Le serveur produit les points-virgules ; la marque appartient à
 * l'octet-à-octet du fichier remis, donc à l'endroit qui le remet.
 */
function remettreLeFichier(nomDeFichier: string, contenu: string): void {
  const MARQUE_DORDRE = '﻿';
  const objet = new Blob([MARQUE_DORDRE + contenu], {
    type: 'text/csv;charset=utf-8',
  });
  const adresse = URL.createObjectURL(objet);
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = nomDeFichier;
  lien.click();
  // **La révocation attend le tour suivant, et ce n'est pas une précaution de style.**
  // Révoquer dans la même tâche que `click()` annule le téléchargement sur certains
  // navigateurs : l'adresse est morte avant que le moteur ait fini de lire l'objet, et le
  // symptôme est **un fichier qui ne se télécharge pas, sans aucune erreur**. Un tour de boucle
  // suffit, et l'adresse ne survit pas au geste — ce qui est la lecture exacte de « rien n'est
  // stocké, aucun lien à durée de vie » : elle ne vaut que dans cet onglet, et plus après.
  setTimeout(() => URL.revokeObjectURL(adresse), 0);
}

/**
 * **La structure est celle de la fiche ouverte, jamais celle d'un sélecteur.**
 *
 * On exporte ce qu'on regarde. Lire la structure courante ici ouvrirait un écart entre la
 * fiche affichée et le fichier remis — et un export qui porte sur une autre structure que
 * celle qu'on lit **ne se voit pas** : le fichier est bien formé, il décrit quelqu'un d'autre.
 * Pour un `orgadmin` les deux coïncident de toute façon, Q534.
 */
export function Exports() {
  const structure = useRecordContext<{ id: string }>();
  const notifier = useNotify();
  const { canAccess: peutExporter } = useCanAccess({
    resource: 'structures',
    action: 'exporter',
  });
  const { canAccess: peutImporter } = useCanAccess({
    resource: 'structures',
    action: 'importer',
  });

  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreursDImport, setErreursDImport] = useState<readonly string[]>([]);
  const [bilanDImport, setBilanDImport] = useState<string | null>(null);

  // **La garde vient AVANT les fermetures qui lisent la structure**, et non avant le rendu :
  // placée plus bas, elle laissait `structure` possiblement absente dans `exporter` et
  // `importer` — le typage l'a dit, et il avait raison. Tous les crochets sont appelés
  // au-dessus, donc leur ordre ne bouge pas.
  if (structure === undefined) return null;

  const exporter = async (quoi: 'comptes' | 'cours') => {
    setEnCours(quoi);
    try {
      const reponse = await deballer(
        quoi === 'comptes'
          ? exporterLesComptes({ path: { id: structure.id } })
          : exporterLesCours({ path: { id: structure.id } }),
      );
      remettreLeFichier(reponse.nomDeFichier, reponse.contenu);
      notifier(`${reponse.lignes} ligne(s) exportée(s). Le geste laisse une trace.`, {
        type: 'info',
      });
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      // **`EXPORT_SEPARATEUR_AMBIGU` est un refus voulu, pas une panne** — D-074 : « les
      // listes multiples tiennent dans une colonne séparées par un caractère **qui
      // n'apparaît pas dans les valeurs** ; un nom de structure étant du texte libre, cette
      // propriété ne se garantit pas, elle se **vérifie**. Le sens du défaut est délibéré :
      // un export refusé se répare en renommant une structure ; **un export ambigu se
      // découvre après avoir servi à facturer.** »
      notifier(
        lue.code === 'EXPORT_SEPARATEUR_AMBIGU'
          ? 'Export refusé : une valeur contient le séparateur de listes, ce qui rendrait le ' +
              'fichier ambigu. Renommez la valeur signalée puis relancez.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(null);
    }
  };

  const importer = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    const formulaire = new FormData(evenement.currentTarget);
    const fichier = formulaire.get('fichier');
    if (!(fichier instanceof File) || fichier.size === 0) {
      notifier('Choisissez un fichier CSV.', { type: 'warning' });
      return;
    }

    setEnCours('import');
    setErreursDImport([]);
    setBilanDImport(null);
    try {
      // **Le corps ne porte QUE le fichier**, et la structure n'y figure pas : la route la
      // déduit de l'appelant. C'est ce qui la rend inatteignable au compte qui voit tout —
      // voir la note de `importer` dans `matrice.ts`, et le refus que le service lève.
      const contenu = await fichier.text();
      const reponse = await deballer(importerDesComptes({ body: { fichier: contenu } }));
      setBilanDImport(
        `${reponse.lignesAcceptees} ligne(s) acceptée(s). Aucun doublon n’est créé : la ` +
          `reconnaissance se fait par adresse.`,
      );
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      // F-66 critère 1 : le fichier entier est refusé, et **toutes** les erreurs sont
      // rendues d'un coup. Les montrer une à une obligerait à repasser le fichier autant de
      // fois qu'il porte de fautes — et chaque passage laisse sa ligne de trace.
      const details = (erreur as { details?: { erreurs?: unknown } } | null)?.details;
      const liste = Array.isArray(details?.erreurs) ? (details.erreurs as unknown[]) : [];
      setErreursDImport(liste.map((entree) => String(entree)));
      notifier(
        lue.code === 'IMPORT_FICHIER_REFUSE'
          ? 'Fichier refusé en entier : une seule ligne invalide suffit. Toutes les erreurs ' +
              'sont listées ci-dessous.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(null);
    }
  };

  if (!peutExporter && !peutImporter) return null;

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-sm font-medium">Exports et import</h3>

      {peutExporter && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={enCours !== null}
              onClick={() => void exporter('comptes')}
            >
              Exporter les comptes
            </Button>
            <Button
              variant="outline"
              disabled={enCours !== null}
              onClick={() => void exporter('cours')}
            >
              Exporter les cours
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            CSV à point-virgule, une ligne par personne, sans plafond de lignes. Rien n’est
            stocké et aucun lien n’est émis : le fichier est remis directement. L’export des
            comptes porte les autres rattachements et les relations extérieures des personnes,
            et jamais l’empreinte du secret d’appel. Les deux gestes laissent une trace
            conservée.
          </p>
        </div>
      )}

      {/* **L'ABSENCE EST UNE RÈGLE DEPUIS LE 20260824, PLUS UN ARBITRAGE EN ATTENTE — D-082.**
          Le droit a été retiré au compte qui voit tout : l'import est un geste de la structure,
          ce que F-60 soutient déjà — « une structure rattache et détache, elle ne crée pas ».
          Le message le DIT quand même, et ne se contente pas de cacher le bouton : la personne
          qui cherche l'import doit trouver **ce qu'elle fait à la place**, sans quoi l'absence
          se lit comme un oubli. « Une absence et un refus se lisent pareil », F-55. */}
      {!peutImporter && peutExporter && (
        <Alert>
          <AlertTitle>L’import est un geste de la structure, pas du parc.</AlertTitle>
          <AlertDescription>
            Un import s’applique à une structure, et ce compte n’a aucun rattachement par
            construction. Depuis ce compte, on crée la personne puis on la rattache : le
            résultat est le même, un geste à la fois. L’import en masse se fait depuis un compte
            qui tient l’espace de la structure.
          </AlertDescription>
        </Alert>
      )}

      {peutImporter && (
        <form className="flex flex-col gap-2" onSubmit={(e) => void importer(e)}>
          <Label htmlFor="fichier">Importer des comptes</Label>
          <div className="flex gap-2">
            <Input id="fichier" name="fichier" type="file" accept=".csv,text/csv" />
            <Button type="submit" disabled={enCours !== null}>
              Importer
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Cinq champs et pas un de plus. Une seule ligne invalide refuse le fichier entier,
            et toutes les erreurs sont montrées d’un coup. Passer deux fois le même fichier ne
            crée aucun doublon — mais écrit deux lignes de trace, parce que le geste a eu lieu
            deux fois.
          </p>

          {bilanDImport !== null && (
            <Alert>
              <AlertTitle>Import accepté</AlertTitle>
              <AlertDescription>{bilanDImport}</AlertDescription>
            </Alert>
          )}

          {erreursDImport.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>Fichier refusé — {erreursDImport.length} erreur(s)</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {erreursDImport.map((ligne, rang) => (
                    <li key={rang}>{ligne}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </form>
      )}
    </section>
  );
}
